import React, { useState, useEffect, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Lock, CheckCircle2, CreditCard, Banknote } from "lucide-react";
import Layout from "@/components/Layout";
import api, { formatINR } from "@/lib/api";
import { useCart } from "@/contexts/CartContext";
import { useAuth } from "@/contexts/AuthContext";

export default function Checkout() {
  const { items, subtotal, clear } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [step, setStep] = useState(1); // 1 address, 2 shipping, 3 payment
  const [address, setAddress] = useState({
    full_name: user?.name || "",
    phone: "",
    line1: "", line2: "",
    city: "", state: "", pincode: "", country: "India",
  });
  const [rates, setRates] = useState(null);
  const [shipping, setShipping] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState("razorpay");
  const [placing, setPlacing] = useState(false);
  const [couponInput, setCouponInput] = useState("");
  const [coupon, setCoupon] = useState(null); // {code, discount, label}
  const [applyingCoupon, setApplyingCoupon] = useState(false);
  const placedRef = useRef(false);

  useEffect(() => {
    if (placedRef.current) return;
    if (items.length === 0) navigate("/cart");
  }, [items.length, navigate]);

  const shippingCost = shipping?.rate || 0;
  const discount = coupon?.discount || 0;
  const total = Math.max(0, subtotal - discount + shippingCost);

  async function applyCoupon() {
    if (!couponInput.trim()) return;
    setApplyingCoupon(true);
    try {
      const { data } = await api.post("/coupons/apply", {
        code: couponInput.trim(), subtotal,
      });
      setCoupon(data);
      toast.success(`${data.label} applied`);
    } catch (e) {
      const msg = e.response?.data?.detail || "Could not apply coupon";
      toast.error(typeof msg === "string" ? msg : "Invalid coupon");
      setCoupon(null);
    } finally {
      setApplyingCoupon(false);
    }
  }

  function removeCoupon() {
    setCoupon(null);
    setCouponInput("");
  }

  async function fetchRates() {
    if (!/^\d{6}$/.test(address.pincode)) { toast.error("Enter a valid 6-digit pincode"); return; }
    try {
      const { data } = await api.post("/shipping/rates", {
        pincode: address.pincode, weight: 1.0, cod: paymentMethod === "cod",
      });
      setRates(data);
      setShipping(data.couriers?.[0] || null);
      setStep(2);
    } catch { toast.error("Could not fetch shipping rates"); }
  }

  function validateAddress() {
    const req = ["full_name", "phone", "line1", "city", "state", "pincode"];
    for (const k of req) if (!address[k]?.trim()) { toast.error(`Please enter your ${k.replace("_", " ")}`); return false; }
    if (!/^\d{10}$/.test(address.phone)) { toast.error("Phone must be 10 digits"); return false; }
    if (!/^\d{6}$/.test(address.pincode)) { toast.error("Pincode must be 6 digits"); return false; }
    return true;
  }

  async function placeOrder() {
    if (!shipping) { toast.error("Please pick a shipping option"); return; }
    setPlacing(true);
    try {
      const orderPayload = {
        items, address,
        shipping_method: shipping.id,
        shipping_cost: shipping.rate,
        subtotal, total,
        payment_method: paymentMethod,
        coupon_code: coupon?.code || null,
        discount,
      };
      const { data: order } = await api.post("/orders", orderPayload);

      if (paymentMethod === "razorpay") {
        // Real PayU checkout — server returns { provider, payment_url, payload }
        const { data: init } = await api.post("/payments/create-order", { order_id: order.id });
        if (init.provider === "payu" && init.payment_url && init.payload) {
          // Auto-submit hidden form to PayU (customer is redirected to PayU hosted page)
          const form = document.createElement("form");
          form.method = "POST";
          form.action = init.payment_url;
          Object.entries(init.payload).forEach(([name, value]) => {
            const input = document.createElement("input");
            input.type = "hidden";
            input.name = name;
            input.value = value ?? "";
            form.appendChild(input);
          });
          document.body.appendChild(form);
          form.submit();
          return; // redirect happens; do not clear cart here
        }
        // Fallback: mock verify (only fires if PayU creds missing on backend)
        await new Promise(r => setTimeout(r, 800));
        await api.post("/payments/verify", {
          order_id: order.id,
          razorpay_payment_id: "pay_mock_" + Math.random().toString(36).slice(2, 14),
          razorpay_order_id: "order_mock_" + Math.random().toString(36).slice(2, 14),
          razorpay_signature: "sig_mock",
        });
      }
      clear();
      placedRef.current = true;
      navigate(`/order/success/${order.id}`, { replace: true });
    } catch (e) {
      toast.error("Could not place order. Try again.");
    } finally {
      setPlacing(false);
    }
  }

  return (
    <Layout hideFooter>
      <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-24 py-12">
        <div className="overline text-[#C9A227]">Secure checkout</div>
        <div className="flex flex-wrap items-end justify-between gap-4 mt-2">
          <h1 className="text-3xl md:text-4xl font-light text-[#163A2F] tracking-tight">Checkout</h1>
          <Link to="/cart" className="text-sm text-[#163A2F]/70 hover:text-[#163A2F]">← Edit bag</Link>
        </div>

        <div className="mt-10 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-7 space-y-10">
            {/* Step 1 - Address */}
            <section className="border hairline p-6 md:p-8">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-medium text-[#163A2F]">1 · Shipping address</h2>
                {step > 1 && <button onClick={() => setStep(1)} className="text-sm underline text-[#163A2F]/70">Edit</button>}
              </div>
              {step === 1 && (
                <div className="mt-6 grid sm:grid-cols-2 gap-4">
                  {[
                    { k: "full_name", l: "Full name", span: 2 },
                    { k: "phone", l: "Phone (10 digit)" },
                    { k: "pincode", l: "Pincode" },
                    { k: "line1", l: "Address line 1", span: 2 },
                    { k: "line2", l: "Address line 2 (optional)", span: 2 },
                    { k: "city", l: "City" },
                    { k: "state", l: "State" },
                  ].map(f => (
                    <div key={f.k} className={f.span === 2 ? "sm:col-span-2" : ""}>
                      <label className="overline text-[#163A2F]/70">{f.l}</label>
                      <input
                        data-testid={`address-${f.k}`}
                        value={address[f.k]}
                        onChange={(e) => setAddress({ ...address, [f.k]: e.target.value })}
                        className="mt-1 w-full px-4 py-3 border hairline bg-transparent focus:outline-none focus:border-[#C9A227]"
                      />
                    </div>
                  ))}
                  <div className="sm:col-span-2">
                    <button
                      onClick={() => { if (validateAddress()) fetchRates(); }}
                      data-testid="continue-to-shipping"
                      className="mt-2 bg-[#163A2F] text-white px-8 py-3.5 uppercase text-xs tracking-[0.25em]"
                    >
                      Continue to shipping
                    </button>
                  </div>
                </div>
              )}
              {step > 1 && (
                <div className="mt-4 text-sm text-[#163A2F]/80">
                  {address.full_name} · {address.phone}<br />
                  {address.line1}, {address.line2 && `${address.line2}, `}{address.city}, {address.state} — {address.pincode}
                </div>
              )}
            </section>

            {/* Step 2 - Shipping */}
            <section className={`border hairline p-6 md:p-8 ${step < 2 ? "opacity-50 pointer-events-none" : ""}`}>
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-medium text-[#163A2F]">2 · Shipping method</h2>
                {step > 2 && <button onClick={() => setStep(2)} className="text-sm underline text-[#163A2F]/70">Edit</button>}
              </div>
              {step === 2 && rates && (
                <div className="mt-6 space-y-3">
                  {rates.couriers.map(c => (
                    <label key={c.id} className={`flex items-center justify-between border hairline p-4 cursor-pointer ${shipping?.id === c.id ? "border-[#C9A227] bg-[#C9A227]/5" : ""}`} data-testid={`ship-option-${c.id}`}>
                      <div className="flex items-center gap-3">
                        <input type="radio" name="ship" checked={shipping?.id === c.id} onChange={() => setShipping(c)} className="accent-[#163A2F]" />
                        <div>
                          <div className="text-[#163A2F] font-medium text-sm">{c.name}</div>
                          <div className="text-xs text-[#163A2F]/60">ETA: {c.eta_days} day{c.eta_days > 1 ? "s" : ""}</div>
                        </div>
                      </div>
                      <div className="font-medium text-[#163A2F]">{formatINR(c.rate)}</div>
                    </label>
                  ))}
                  <button onClick={() => setStep(3)} disabled={!shipping} data-testid="continue-to-payment" className="bg-[#163A2F] text-white px-8 py-3.5 uppercase text-xs tracking-[0.25em] disabled:opacity-40">Continue to payment</button>
                </div>
              )}
              {step > 2 && shipping && (
                <div className="mt-4 text-sm text-[#163A2F]/80">{shipping.name} · {formatINR(shipping.rate)}</div>
              )}
            </section>

            {/* Step 3 - Payment */}
            <section className={`border hairline p-6 md:p-8 ${step < 3 ? "opacity-50 pointer-events-none" : ""}`}>
              <h2 className="text-xl font-medium text-[#163A2F]">3 · Payment</h2>
              {step === 3 && (
                <div className="mt-6 space-y-4">
                  <label className={`flex items-start gap-4 border hairline p-4 cursor-pointer ${paymentMethod === "razorpay" ? "border-[#C9A227] bg-[#C9A227]/5" : ""}`} data-testid="pay-razorpay">
                    <input type="radio" checked={paymentMethod === "razorpay"} onChange={() => setPaymentMethod("razorpay")} className="mt-1 accent-[#163A2F]" />
                    <div className="flex-1">
                      <div className="flex items-center gap-2 text-[#163A2F] font-medium"><CreditCard className="w-4 h-4" /> PayU <span className="text-[10px] uppercase tracking-widest bg-[#C9A227] text-white px-2 py-0.5">Test mode</span></div>
                      <div className="text-xs text-[#163A2F]/60 mt-1">UPI · Cards · Net banking · Wallets · EMI · International</div>
                    </div>
                  </label>
                  <label className={`flex items-start gap-4 border hairline p-4 cursor-pointer ${paymentMethod === "cod" ? "border-[#C9A227] bg-[#C9A227]/5" : ""}`} data-testid="pay-cod">
                    <input type="radio" checked={paymentMethod === "cod"} onChange={() => setPaymentMethod("cod")} className="mt-1 accent-[#163A2F]" />
                    <div className="flex-1">
                      <div className="flex items-center gap-2 text-[#163A2F] font-medium"><Banknote className="w-4 h-4" /> Cash on delivery</div>
                      <div className="text-xs text-[#163A2F]/60 mt-1">Pay in cash when your parcel arrives.</div>
                    </div>
                  </label>

                  <button
                    onClick={placeOrder} disabled={placing}
                    data-testid="place-order-button"
                    className="w-full bg-[#C9A227] text-white py-4 uppercase text-xs tracking-[0.25em] hover:bg-[#DCC267] transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {placing ? "Processing…" : (<><Lock className="w-4 h-4" /> Place order · {formatINR(total)}</>)}
                  </button>
                  <p className="text-[11px] text-[#163A2F]/55 text-center">Mock payment for demo. Plug in real Razorpay keys when ready.</p>
                </div>
              )}
            </section>
          </div>

          <aside className="lg:col-span-5">
            <div className="border hairline p-6 sticky top-28">
              <div className="overline text-[#C9A227] mb-4">Order summary</div>
              <div className="space-y-4 max-h-72 overflow-y-auto pr-2">
                {items.map(i => (
                  <div key={`${i.product_id}-${i.size}-${i.color}`} className="flex gap-3">
                    <img src={i.image} alt={i.name} className="w-14 h-18 object-cover bg-[#163A2F]/5" />
                    <div className="flex-1 text-sm">
                      <div className="text-[#163A2F] font-medium leading-snug">{i.name}</div>
                      <div className="text-xs text-[#163A2F]/60">{i.size} · {i.color || "Default"} · Qty {i.quantity}</div>
                    </div>
                    <div className="text-sm font-medium text-[#163A2F]">{formatINR(i.price * i.quantity)}</div>
                  </div>
                ))}
              </div>
              <div className="mt-6 space-y-3 text-[#163A2F] text-sm">
                <div className="flex justify-between"><span>Subtotal</span><span>{formatINR(subtotal)}</span></div>
                {discount > 0 && (
                  <div className="flex justify-between text-[#C9A227]">
                    <span>Discount ({coupon.code})</span>
                    <span data-testid="checkout-discount">− {formatINR(discount)}</span>
                  </div>
                )}
                <div className="flex justify-between"><span>Shipping</span><span>{shipping ? formatINR(shippingCost) : "—"}</span></div>
              </div>

              {/* Coupon input */}
              <div className="mt-5 border-t hairline pt-5">
                {coupon ? (
                  <div className="flex items-center justify-between bg-[#C9A227]/10 border border-[#C9A227]/40 px-3 py-2.5">
                    <div className="text-xs">
                      <div className="text-[#163A2F] font-medium uppercase tracking-widest">{coupon.code}</div>
                      <div className="text-[#163A2F]/60">{coupon.label}</div>
                    </div>
                    <button onClick={removeCoupon} data-testid="remove-coupon" className="text-xs underline text-[#163A2F]/70">Remove</button>
                  </div>
                ) : (
                  <div>
                    <label className="overline text-[#163A2F]/70">Promo code</label>
                    <div className="mt-1 flex gap-2">
                      <input
                        value={couponInput}
                        onChange={(e) => setCouponInput(e.target.value.toUpperCase())}
                        placeholder="Enter code (e.g. NORBU10)"
                        data-testid="coupon-input"
                        className="flex-1 px-3 py-2.5 border hairline bg-transparent focus:outline-none focus:border-[#C9A227] uppercase tracking-widest text-sm"
                      />
                      <button
                        onClick={applyCoupon}
                        disabled={applyingCoupon || !couponInput.trim()}
                        data-testid="apply-coupon"
                        className="px-5 bg-[#163A2F] text-white text-xs uppercase tracking-[0.2em] disabled:opacity-40"
                      >
                        {applyingCoupon ? "…" : "Apply"}
                      </button>
                    </div>
                    <p className="text-[11px] text-[#163A2F]/55 mt-1.5">Use <span className="font-medium text-[#C9A227]">NORBU10</span> for 10% off your first order.</p>
                  </div>
                )}
              </div>

              <div className="my-4 gold-rule" />
              <div className="flex justify-between font-medium text-lg text-[#163A2F]">
                <span>Total</span><span data-testid="checkout-total">{formatINR(total)}</span>
              </div>
              <div className="mt-4 flex items-center gap-2 text-xs text-[#163A2F]/60"><Lock className="w-3.5 h-3.5" /> Secure SSL checkout</div>
            </div>
          </aside>
        </div>
      </div>
    </Layout>
  );
}
